import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Bot, Copy, Download, Hand, Lock, MessageCircle, RefreshCw, Send, Trash2, User } from 'lucide-react';

import useAdminChatConversation from '../../hooks/useAdminChatConversation';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { parseApiError } from '../../utils/apiError';
import { formatChatTranscript } from '../../utils/chatTranscript';

// The interactive half of the staff console (chat_bot_implementation.md
// Phase 8) — what ChatConsole.js's overview links to via its "Interact"
// button. One conversation's full transcript, refresh, take-over/hand-back,
// staff reply, and delete (blocked server-side while mode is 'human' — see
// chatbotFunctions.adminDeleteConversation).
export default function AdminChatInteract() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [togglingMode, setTogglingMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    conversation,
    messages,
    loading,
    connected,
    deleted,
    reload,
    setMode: setConversationMode,
    sendMessage,
    deleteConversation,
  } = useAdminChatConversation(id);
  const selfDeletedRef = useRef(false);

  // The customer deleted it, or another rep did, while this tab had it
  // open — nothing left to show or reply to, so bail out to the list. Skips
  // itself when this tab is the one that just deleted it (onDelete below
  // already navigates/toasts for that case, and its own socket is in this
  // conversation's room too, so it receives the same broadcast back).
  useEffect(() => {
    if (!deleted || selfDeletedRef.current) return;
    addToast('This conversation was deleted', { variant: 'error' });
    navigate('/admin/chat');
  }, [deleted, addToast, navigate]);

  const onToggleMode = async (nextMode) => {
    setTogglingMode(true);
    try {
      await setConversationMode(nextMode);
      addToast(nextMode === 'human' ? 'Conversation assigned to you' : 'Handed back to the bot', { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not update the conversation', { variant: 'error' });
    } finally {
      setTogglingMode(false);
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: 'Delete this conversation?',
      message: 'This permanently deletes the conversation and its messages for this customer. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    selfDeletedRef.current = true;
    try {
      await deleteConversation();
      addToast('Conversation deleted', { variant: 'success' });
      navigate('/admin/chat');
    } catch (err) {
      selfDeletedRef.current = false;
      addToast(parseApiError(err).formError || 'Could not delete the conversation', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const getMessageLabel = (m) => (m.role === 'user' ? 'Customer' : m.author === 'human' ? 'Staff' : 'Bot');
  const transcriptTitle = conversation?.title || 'Untitled conversation';

  const onCopyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(formatChatTranscript(messages, getMessageLabel, transcriptTitle));
      addToast('Conversation copied to clipboard', { variant: 'success' });
    } catch {
      addToast('Could not copy the conversation', { variant: 'error' });
    }
  };

  const onDownloadTranscript = () => {
    const blob = new Blob([formatChatTranscript(messages, getMessageLabel, transcriptTitle)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sustainably-sprouted-chat-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onSend = async (e) => {
    e.preventDefault();
    const trimmed = reply.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await sendMessage(trimmed);
      setReply('');
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not send reply', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Link to="/admin/chat" className="ss-back-link">
        <ArrowLeft size={16} aria-hidden="true" /> Back to Chat Handoff
      </Link>

      <h1 style={{ marginTop: 'var(--ss-space-2)' }}>Chat Interact</h1>

      <Card style={{ marginTop: 'var(--ss-space-4)' }} className="ss-chat-console__detail">
        {loading ? (
          <div className="ss-state">
            <Spinner label="Loading conversation" size={28} />
          </div>
        ) : (
          <>
            <div className="ss-chat-console__detail-head">
              {conversation?.handoffRequested && (
                <span
                  className="ss-chat-console__status-icon ss-chat-console__status-icon--handoff"
                  title={
                    conversation.handoffRequestedAt
                      ? `Person requested — ${new Date(conversation.handoffRequestedAt).toLocaleString()}`
                      : 'Person requested'
                  }
                >
                  <Hand size={18} aria-hidden="true" />
                </span>
              )}
              <span
                className={`ss-chat-console__status-icon ss-chat-console__status-icon--${
                  conversation?.status === 'archived' ? 'closed' : 'open'
                }`}
                title={conversation?.status === 'archived' ? 'Closed' : 'Open'}
              >
                {conversation?.status === 'archived' ? (
                  <Lock size={18} aria-hidden="true" />
                ) : (
                  <MessageCircle size={18} aria-hidden="true" />
                )}
              </span>
              <span
                className="ss-chat-console__status-icon"
                title={conversation?.mode === 'human' ? 'Person' : 'Bot'}
              >
                {conversation?.mode === 'human' ? (
                  <User size={18} aria-hidden="true" />
                ) : (
                  <Bot size={18} aria-hidden="true" />
                )}
              </span>
              <div className="ss-chat-console__detail-title">
                <h2>{conversation?.title || 'Untitled conversation'}</h2>
                <span className="ss-chat-console__detail-userid">
                  User ID: {conversation?.user?._id || conversation?.user}
                </span>
              </div>
              <div className="ss-admin-actions">
                <span className="ss-chat-console__detail-actions-group">
                  <button
                    type="button"
                    className="ss-icon-btn"
                    onClick={onCopyTranscript}
                    aria-label="Copy conversation"
                    title="Copy conversation"
                  >
                    <Copy size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ss-icon-btn"
                    onClick={onDownloadTranscript}
                    aria-label="Download conversation"
                    title="Download conversation"
                  >
                    <Download size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={`ss-icon-btn ${!connected ? 'ss-icon-btn--warn' : ''}`.trim()}
                    onClick={reload}
                    aria-label={connected ? 'Refresh' : 'Disconnected — click to refresh'}
                    title={connected ? 'Refresh' : 'Live updates disconnected — click to refresh'}
                  >
                    <RefreshCw size={16} aria-hidden="true" />
                  </button>
                  {conversation?.mode === 'human' ? (
                    <Button variant="ghost" size="sm" disabled={togglingMode} onClick={() => onToggleMode('bot')}>
                      Hand back
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={togglingMode || conversation?.status === 'archived'}
                      title={conversation?.status === 'archived' ? 'This conversation is closed' : undefined}
                      onClick={() => onToggleMode('human')}
                    >
                      Take over
                    </Button>
                  )}
                </span>
                <button
                  type="button"
                  className="ss-icon-btn ss-icon-btn--danger"
                  disabled={deleting || conversation?.mode === 'human'}
                  title={conversation?.mode === 'human' ? 'Hand back to the bot before deleting' : undefined}
                  onClick={onDelete}
                  aria-label="Delete conversation"
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="ss-chat-console__transcript">
              {messages.map((m) => (
                <div key={m._id} className={`ss-chat-console__msg ss-chat-console__msg--${m.role === 'user' ? 'user' : 'assistant'}`}>
                  <span className="ss-chat-console__msg-label">
                    {m.role === 'user' ? (
                      'Customer'
                    ) : m.author === 'human' ? (
                      'Staff'
                    ) : (
                      <>
                        <Bot size={12} aria-hidden="true" /> Bot
                      </>
                    )}
                    {m.createdAt && (
                      <span className="ss-chat-console__msg-time">
                        {new Date(m.createdAt).toLocaleString()}
                      </span>
                    )}
                  </span>
                  {m.toolCalls?.length > 0 && !m.content ? (
                    <span className="ss-chat-console__msg-tool">Looked something up…</span>
                  ) : (
                    <span>{m.content}</span>
                  )}
                </div>
              ))}
            </div>

            <form className="ss-chat-console__reply" onSubmit={onSend}>
              <textarea
                className="ss-input"
                rows={2}
                placeholder={conversation?.mode === 'human' ? 'Reply as staff…' : 'Take over to reply directly'}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                disabled={conversation?.mode !== 'human' || sending}
              />
              <Button type="submit" disabled={conversation?.mode !== 'human' || sending || !reply.trim()}>
                <Send size={16} aria-hidden="true" />
              </Button>
            </form>
          </>
        )}
      </Card>
      {dialog}
    </div>
  );
}
